import React, { useCallback, useMemo } from 'react';
import { FlatListProps, ListRenderItem, RefreshControl } from 'react-native';
import styled, { useTheme } from 'styled-components/native';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { ViewStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import { Box } from '@/components/base/box';
import { AppSpinner } from '@/components/base/app.spinner';
import { AppListView } from '@/components/base/app.list.view';
import { AppTextView } from '@/components/base/app.text.view';
import { AppButton } from '@/components/base/app.button';
import { LoaderContainer } from '@/components/compose/loader.container';

interface Props extends Omit<FlatListProps<any>, 'renderItem' | 'data'> {
  api: UseInfiniteQueryResult<any, any>;
  horizontal?: boolean;
  scrollBar?: boolean;
  render: ListRenderItem<any>;
  ItemSeparatorComponent?: React.ComponentType<any>;
  convertData?: (data: any[]) => any[];
  keyExtractor?: FlatListProps<any>['keyExtractor'];
  initialNumToRender?: number;
  maxToRenderPerBatch?: number;
  style?: ViewStyle;
  dataOverrides?: any[];
  emptyHint?: boolean;
}
function BaseInfiniteLoaderList({
  api,
  horizontal,
  render,
  dataOverrides,
  ItemSeparatorComponent,
  convertData,
  keyExtractor,
  initialNumToRender,
  maxToRenderPerBatch,
  style,
  emptyHint = true,
  scrollBar = false,
  ...rest
}: Props) {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch, isError } = api;
  const theme = useTheme();

  const allData = useMemo(() => {
    if (dataOverrides) {
      return dataOverrides;
    }
    if (data?.pages) {
      const arr = data.pages.flatMap((page) => [...page.docs]);
      return convertData ? convertData(arr) : arr;
    }
    return [];
  }, [data, convertData, dataOverrides]);

  const nextPage = useCallback(() => {
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, isFetchingNextPage, fetchNextPage]);

  const defaultExtractor = useCallback((item, index) => item._id || item.id || String(index), []);

  const renderFooter = useCallback(() => {
    // If there is absolutely no more data to ever fetch, just add a tiny bottom padding
    if (!hasNextPage || allData.length === 0) {
      return <Box height={20} />;
    }

    // ALWAYS render this container if another page exists to reserve the physical space.
    // Only conditionally render the spinner inside of it.
    return (
      <Box height={60} width="100%" alignItems="center" justifyContent="center">
        {isFetchingNextPage ? <AppSpinner size={'large'} /> : null}
      </Box>
    );
  }, [hasNextPage, allData.length, isFetchingNextPage]);

  if (isFetching && !allData.length) {
    return <LoadingContainer apis={[api]} {...rest} />;
  }

  if (isError && !isFetching && !allData.length) {
    return (
      <FullFlexBox {...rest}>
        <AppTextView text={'مشکلی پیش آمد ، لطفا دوباره تلاش کنید...'} textColor={'black_light'} />
        <RetryButton title={'تلاش دوباره'} mode={'text'} onPress={() => refetch()} variant={'orange'} fontSize={'small'} />
      </FullFlexBox>
    );
  }

  if (emptyHint && !isFetching && !isError && !allData.length) {
    return (
      <FullFlexBox {...rest}>
        <AppTextView text={'چیزی برای نمایش دادن وجود نداره :)'} textColor={'black_light'} />
      </FullFlexBox>
    );
  }

  return (
    <AppListView
      contentContainerStyle={{
        paddingBottom: horizontal ? 0 : 15,
        paddingRight: horizontal ? 15 : 0,
        ...style,
      }}
      horizontal={horizontal}
      showsHorizontalScrollIndicator={scrollBar}
      showsVerticalScrollIndicator={scrollBar}
      keyExtractor={keyExtractor || defaultExtractor}
      data={allData}
      windowSize={21}
      renderItem={render}
      onEndReached={nextPage}
      onEndReachedThreshold={0.5}
      ItemSeparatorComponent={ItemSeparatorComponent}
      initialNumToRender={initialNumToRender}
      maxToRenderPerBatch={maxToRenderPerBatch}
      ListFooterComponent={renderFooter}
      refreshControl={<RefreshControl onRefresh={refetch} refreshing={isFetching && !isFetchingNextPage} colors={theme.colors.activityIndicator.composed} />}
      {...rest}
    />
  );
}

const LoadingContainer = styled(LoaderContainer)`
  flex: 1;
`;

const FullFlexBox = styled(Box)`
  flex: 1;
  align-items: center;
  justify-content: center;
`;

const RetryButton = styled(AppButton)`
  margin-top: 7px;
`;

export const InfiniteLoaderList = React.memo(BaseInfiniteLoaderList);
